// 🔧 pending_entries.js
// ✅ Purpose: Admin/manager page for reviewing public entry submissions
// ✅ Features:
// - Loads pending public entries from /events/{eventId}/pendingEntries
// - Shows submitted name, email, title, description, social links, and uploaded images
// - Allows manager to select a category before approval
// - Approves pending entry into /events/{eventId}/cars
// - Assigns next registration number on approval
// - Copies uploaded image URLs into approved entry record
// - Uses first uploaded image as primary imgUrl
// - Marks pending submission as approved after successful approval
// - Allows manager to reject pending submissions
// - Uses newer responsive Let's Go Vote layout standard
// - Shows success/error notifications for 20 seconds

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { Link } from "react-router-dom";

import { reviewPublicEntry } from "../../services/entry_registration";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import { useEventContext } from "../../hooks/use_event_context";
import { useCategories } from "../../hooks/use_categories";
import { useRole } from "../../role_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";

const PendingEntries = () => {
  const {
    eventslug,
    eventId,
    eventLoading,
    ready,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const { role, eventRole, roleLoading } = useRole();
  const { categories, loading: catsLoading } = useCategories(eventId);

  const [pendingEntries, setPendingEntries] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [transactionIds, setTransactionIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [status, setStatus] = useState(null);

  const canAccess =
    role === "admin" ||
    role === "manager" ||
    eventRole === "manager" ||
    eventRole === "admin";

  const filteredCategories = useMemo(() => {
    return [...categories]
      .filter((cat) => Number(cat.id) !== 0 && !cat.placeholder)
      .sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""))
      );
  }, [categories]);

  const getSubmissionCategory = (submission) => {
    const requested = categories.find((category) => String(category.firestoreId || category.id) === submission.requestedCategoryId);
    return selectedCategories[submission.id] ?? (requested ? String(requested.id) : "__default__");
  };

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  const loadPendingEntries = useCallback(async () => {
    if (!eventId) return;

    setLoading(true);

    try {
      const pendingQuery = query(
        collection(db, "events", eventId, "pendingEntries"),
        where("status", "==", "pending")
      );

      const pendingSnap = await getDocs(pendingQuery);

      const pendingData = pendingSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      setPendingEntries(pendingData);

      logger.log("🕒 Pending entries loaded:", {
        value: pendingData.length,
      });
    } catch (err) {
      logger.error("❌ Failed to load pending entries:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to load pending entries.",
      });
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (!eventId || roleLoading || catsLoading || !canAccess) return;

    loadPendingEntries();
  }, [eventId, roleLoading, catsLoading, canAccess, loadPendingEntries]);

  const handleApprove = async (pendingEntry) => {
    const selectedCategory = getSubmissionCategory(pendingEntry);

    if (!selectedCategory && filteredCategories.some((category) => !category.isDefault)) {
      showStatus(setStatus, {
        type: "warning",
        message: "Please select a category before approving this entry.",
      });

      return;
    }

    setProcessingId(pendingEntry.id);

    try {
      const { regcnt: nextRegCnt, entryId } = await reviewPublicEntry(
        eventId, pendingEntry.id, "approve", selectedCategory,
        transactionIds[pendingEntry.id] ?? pendingEntry.transactionId ?? ""
      );
      // Await the existing trusted allocator before declaring approval complete.
      // Retrying after a slug failure reuses the already approved official entry.
      await httpsCallable(functions, "updateEntrySlug")({ eventId, entryId });

      setPendingEntries((prev) =>
        prev.filter((entry) => entry.id !== pendingEntry.id)
      );

      showStatus(setStatus, {
        type: "success",
        message: `Entry approved as Registration #${nextRegCnt}.`,
      });
    } catch (err) {
      logger.error("❌ Failed to approve pending entry:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to approve pending entry.",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (pendingEntry) => {
    const confirmed = window.confirm(
      `Reject "${pendingEntry.entryTitle || "Untitled Entry"}"?`
    );

    if (!confirmed) return;

    setProcessingId(pendingEntry.id);

    try {
      await reviewPublicEntry(eventId, pendingEntry.id, "reject");

      setPendingEntries((prev) =>
        prev.filter((entry) => entry.id !== pendingEntry.id)
      );

      showStatus(setStatus, {
        type: "success",
        message: "Pending entry rejected.",
      });
    } catch (err) {
      logger.error("❌ Failed to reject pending entry:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to reject pending entry.",
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (!ready || eventLoading || roleLoading || catsLoading || (canAccess && loading)) {
    return <FullPageSpinner message="Loading pending entries..." />;
  }

  if (!canAccess) {
    return (
      <section id="top-content" className="container-fluid py-4">
        <div className="container" style={{ maxWidth: "800px" }}>
          <div className="alert alert-danger">
            You do not have permission to review pending entries.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-4">
      <div className="container" style={{ maxWidth: "1100px" }}>
        <div className="text-center mb-4">
          <h1 className="fw-bold mb-2">Pending Entries</h1>

          <p className="text-muted mb-0">
            Review public entry submissions before they become voting entries.
          </p>
        </div>

        {status?.message && (
          <div
            className={`alert text-center ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <Link to={`/${eventslug}/entry`} className="btn btn-outline-secondary">
            Back to Entries
          </Link>

          <button
            className="btn btn-outline-primary"
            onClick={loadPendingEntries}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {pendingEntries.length === 0 ? (
          <div className="card shadow-sm">
            <div className="card-body text-center">
              <h2 className="h5 fw-bold mb-2">No Pending Entries</h2>

              <p className="text-muted mb-0">
                Public entry submissions that need approval will appear here.
              </p>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {pendingEntries.map((pendingEntry) => {
              const images = Array.isArray(pendingEntry.images)
                ? pendingEntry.images
                : [];

              const primaryImage = images[0];

              return (
                <div key={pendingEntry.id} className="col-12">
                  <div className="card shadow-sm">
                    <div className="card-header bg-light d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                      <div>
                        <h2 className="h5 fw-bold mb-1">
                          {pendingEntry.entryTitle || "Untitled Entry"}
                        </h2>

                        <p className="text-muted mb-0">
                          Submitted by{" "}
                          {`${pendingEntry.firstName || ""} ${pendingEntry.lastName || ""}`.trim() ||
                            "Unknown"}
                          {" — "}
                          {pendingEntry.email || pendingEntry.userEmail || "No email"}
                        </p>
                      </div>

                      <span className="badge bg-warning text-dark">
                        Pending
                      </span>
                    </div>

                    <div className="card-body">
                      <div className="row g-4">
                        <div className="col-12 col-lg-4">
                          <div
                            className="border rounded bg-light d-flex align-items-center justify-content-center"
                            style={{
                              minHeight: "220px",
                            }}
                          >
                            {primaryImage?.url ? (
                              <img
                                src={primaryImage.url}
                                alt={pendingEntry.entryTitle || "Pending Entry"}
                                className="img-fluid rounded"
                                style={{
                                  maxHeight: "220px",
                                  objectFit: "contain",
                                }}
                              />
                            ) : (
                              <span className="text-muted">
                                No Image Uploaded
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-12 col-lg-8">
                          {pendingEntry.description && (
                            <div className="mb-3">
                              <div className="fw-bold">Description</div>
                              <div>{pendingEntry.description}</div>
                            </div>
                          )}

                          <div className="row g-3 mb-3">
                            <div className="col-12 col-md-6">
                              <div className="fw-bold">Facebook</div>
                              <div className="text-muted">
                                {pendingEntry.facebook || "Not provided"}
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="fw-bold">Instagram</div>
                              <div className="text-muted">
                                {pendingEntry.instagram || "Not provided"}
                              </div>
                            </div>
                          </div>

                          {images.length > 1 && (
                            <div className="mb-3">
                              <div className="fw-bold mb-2">
                                Additional Images
                              </div>

                              <div className="row g-2">
                                {images.slice(1).map((image, index) => (
                                  <div
                                    key={`${pendingEntry.id}-image-${index}`}
                                    className="col-4 col-md-3"
                                  >
                                    <img
                                      src={image.url}
                                      alt={`Additional upload ${index + 2}`}
                                      className="img-fluid rounded border"
                                      style={{
                                        width: "100%",
                                        height: "90px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="mb-3">
                            <label htmlFor={`transaction-${pendingEntry.id}`} className="form-label fw-bold">Transaction ID</label>
                            <input id={`transaction-${pendingEntry.id}`} type="text" className="form-control" maxLength={200}
                              value={transactionIds[pendingEntry.id] ?? pendingEntry.transactionId ?? ""}
                              onChange={(event) => setTransactionIds((previous) => ({ ...previous, [pendingEntry.id]: event.target.value }))}
                              disabled={processingId !== null} aria-describedby={`transaction-help-${pendingEntry.id}`} />
                            <div id={`transaction-help-${pendingEntry.id}`} className="form-text">
                              Optional receipt identifier for matching this entry to its event entry receipt. Confirm or correct before approval; this does not verify payment.
                            </div>
                          </div>
                          <div className="mb-3">
                            <label htmlFor={`category-${pendingEntry.id}`} className="form-label fw-bold">
                              Approval Category
                            </label>

                            <select
                              id={`category-${pendingEntry.id}`}
                              className="form-select"
                              value={getSubmissionCategory(pendingEntry)}
                              onChange={(e) =>
                                setSelectedCategories((prev) => ({
                                  ...prev,
                                  [pendingEntry.id]: e.target.value,
                                }))
                              }
                            >
                              <option value="__default__">No category (default)</option>

                              {filteredCategories.filter((cat) => !cat.isDefault).map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.isDefault ? "No category" : cat.name}
                                </option>
                              ))}
                            </select>

                            <div className="form-text">
                              Confirm the requested category or choose the default or another category before approval.
                            </div>
                          </div>

                          <div className="d-flex flex-wrap gap-2">
                            <button
                              className="btn btn-success"
                              onClick={() => handleApprove(pendingEntry)}
                              disabled={processingId !== null}
                            >
                              {processingId === pendingEntry.id
                                ? "Processing..."
                                : "✅ Approve Entry"}
                            </button>

                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleReject(pendingEntry)}
                              disabled={processingId !== null}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PendingEntries;
